import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Computer, 
  Palette, 
  PenTool, 
  FileText, 
  GraduationCap,
  Search,
  Filter,
  ArrowLeft,
  Camera,
  Music,
  Languages,
  HeartPulse,
  Shirt,
  ShoppingBag,
  Home,
  Gamepad2,
  TrendingUp,
  Cog
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useServices } from "@/hooks/useServices";
import RequestList from '@/components/RequestList';

// Category icons map
const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  "Coding": Computer,
  "Design": PenTool,
  "Writing": FileText,
  "Marketing and Sales": TrendingUp,
  "Gaming": Gamepad2,
  "Tutoring": GraduationCap,
  "Photography": Camera,
  "Music": Music,
  "Language": Languages,
  "Chores": HeartPulse,
  "Fashion": Shirt,
  "Business": ShoppingBag,
  "Arts": Palette,
  "Engineering and CAD": Cog,
  "Other": Computer
};

// Category colors for circular backgrounds
const categoryColors: Record<string, string> = {
  "Coding": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Gaming": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Chores": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

// Category order
const categoryOrder = [
  "Coding",
  "Design", 
  "Writing",
  "Marketing and Sales",
  "Gaming",
  "Tutoring",
  "Photography",
  "Music",
  "Language",
  "Chores",
  "Fashion",
  "Business",
  "Arts",
  "Engineering and CAD",
  "Other"
];

// Calculate cards to show for exactly 2 rows based on current screen width
const calculateCardsToShow = () => {
  const width = window.innerWidth;
  
  let cardsPerRow;
  if (width >= 1536) cardsPerRow = 8;      // 2xl
  else if (width >= 1280) cardsPerRow = 7; // xl
  else if (width >= 1024) cardsPerRow = 6; // lg
  else if (width >= 768) cardsPerRow = 5;  // md
  else if (width >= 480) cardsPerRow = 4;  // sm
  else cardsPerRow = 3;                    // xs
  
  return cardsPerRow * 2; // Always show exactly 2 rows
};

const Requests = () => {
  const { categories, isCategoriesLoading } = useServices();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [cardsToShow, setCardsToShow] = useState(calculateCardsToShow());
  
  // Set up resize listener to update cards to show when window size changes
  useEffect(() => {
    const handleResize = () => {
      const newCardsToShow = calculateCardsToShow();
      setCardsToShow(newCardsToShow);
    };

    window.addEventListener('resize', handleResize);
    
    // Initial calculation
    const initialCardsToShow = calculateCardsToShow();
    setCardsToShow(initialCardsToShow);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setPriceRange("");
    setDeliveryMethod("");
  };
  
  // Get category name by ID
  const getCategoryNameById = (id: string | null) => {
    if (!id) return "Uncategorized";
    const category = categories?.find(cat => cat.id === id);
    return category ? category.name : "Uncategorized";
  };

  // Sort categories by the defined order
  const sortedCategories = categories?.sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a.name);
    const bIndex = categoryOrder.indexOf(b.name);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  // Show calculated number of cards for two rows unless showAll is true
  const categoriesToShow = showAllCategories ? sortedCategories : sortedCategories?.slice(0, cardsToShow);
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="container mx-auto px-4 py-4 flex-grow">
        <div className="flex items-center mb-3">
          <Link to="/" className="flex items-center text-muted-foreground hover:text-anyskill-purple mr-4">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-poppins font-bold text-xl md:text-2xl">
            Service <span className="text-anyskill-blue">Requests</span>
          </h1>
        </div>

        {/* Compact Search and Filters */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Search service requests..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[120px] h-8">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {sortedCategories?.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue placeholder="Price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Price</SelectItem>
                  <SelectItem value="0-25">$0 - $25</SelectItem>
                  <SelectItem value="25-50">$25 - $50</SelectItem>
                  <SelectItem value="50-100">$50 - $100</SelectItem>
                  <SelectItem value="100+">$100+</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="in-person">In-Person</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8">
                    <Filter className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64">
                  <div className="grid gap-3">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Location</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="local">Local Only</SelectItem>
                          <SelectItem value="remote">Remote Only</SelectItem>
                          <SelectItem value="any-location">Any Location</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Availability</h4>
                      <Select>
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Available Now</SelectItem>
                          <SelectItem value="weekdays">Weekdays</SelectItem>
                          <SelectItem value="weekends">Weekends</SelectItem>
                          <SelectItem value="any-time">Any Time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              
              {(searchQuery || selectedCategory || priceRange || deliveryMethod) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-muted-foreground h-8"
                  onClick={handleResetFilters}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Categories Grid */}
        {isCategoriesLoading ? (
          <div className="flex justify-center my-4">
            <p className="text-sm">Loading categories...</p>
          </div>
        ) : (
          <div className="mb-6">
            <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-2">
              {categoriesToShow?.map((category) => {
                const IconComponent = categoryIcons[category.name] || Computer;
                const colorClass = categoryColors[category.name] || "bg-gray-500 text-white";
                
                return (
                  <Link key={category.id} to={`/requests/category/${category.id}`}>
                    <Card className="hover:shadow-md transition-shadow cursor-pointer dark:bg-muted/50">
                      <CardContent className="p-2 text-center h-20 flex flex-col items-center justify-center gap-1">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                          <IconComponent className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <CardTitle className="text-xs font-medium leading-tight line-clamp-2 break-words">{category.name}</CardTitle>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {/* Show All Button */}
            {sortedCategories && sortedCategories.length > cardsToShow && (
              <div className="text-center mt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAllCategories(!showAllCategories)}
                >
                  {showAllCategories ? "Show Less" : `Show All (${sortedCategories.length - cardsToShow} more)`}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Service Requests List */}
        <div>
          <h2 className="font-poppins font-semibold text-xl mb-4">
            {selectedCategory && categories?.length > 0 ? (
              <>Service Requests in {getCategoryNameById(selectedCategory)}</>
            ) : (
              'Service Requests'
            )}
          </h2>
          
          <RequestList 
            categoryId={selectedCategory === "all" ? undefined : selectedCategory}
            searchQuery={searchQuery}
            priceRange={priceRange}
            deliveryMethod={deliveryMethod}
          />
        </div>
      </div>
    </div>
  );
};

export default Requests;
